import { useEffect, useState, useMemo } from "react"
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { useCurrency } from "@/context/currency/currency-context"
import { getPerformanceTimeseries, getPerformanceSummary, refreshPerformanceCache } from "@/lib/api/hive"
import { PERFORMANCE_PERIODS } from "@/types/performance"
import type { PerformancePeriod, TimeSeriesPoint, PerformanceSummary } from "@/types/performance"
import { generateDemoPerformance } from "@/lib/performance-math"
import { PeriodSelector } from "@/components/custom/dashboard/period-selector"
import { PerformanceChartSkeleton } from "@/components/custom/skeletons"
import { cn } from "@/lib/utils"
import { RefreshCw } from "lucide-react"

interface PerformanceSectionProps {
	portfolioId?: string
	demo?: boolean
}

function formatAxisDate(dateStr: string, period: string): string {
	const date = new Date(dateStr + "T00:00:00")
	const shortPeriods = ["1W", "1M", "3M"]
	if (shortPeriods.includes(period)) {
		return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
	}
	return date.toLocaleDateString("en-US", { month: "short" })
}

function formatTooltipDate(dateStr: string): string {
	const date = new Date(dateStr + "T00:00:00")
	return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

interface PerformanceTooltipProps {
	active?: boolean
	payload?: { value: number; payload: TimeSeriesPoint }[]
	label?: string
	formatFn: (v: number, options?: { compact?: boolean; skipConversion?: boolean }) => string
	allData: TimeSeriesPoint[]
}

function PerformanceTooltip({ active, payload, formatFn, allData }: PerformanceTooltipProps) {
	if (!active || !payload || !payload.length) return null

	const point = payload[0].payload
	const value = payload[0].value
	const idx = allData.findIndex((d) => d.date === point.date)
	const prev = idx > 0 ? allData[idx - 1] : null
	const dailyChange = prev ? value - prev.value : 0
	const dailyChangePct = prev && prev.value !== 0 ? (dailyChange / prev.value) * 100 : 0

	return (
		<div className="bg-background/95 backdrop-blur-sm border border-border p-3 rounded-lg shadow-lg text-sm">
			<p className="font-semibold text-sky-900 dark:text-sky-100 mb-1">{formatTooltipDate(point.date)}</p>
			<p className="font-bold text-foreground">{formatFn(value, { skipConversion: true })}</p>
			{prev && (
				<p
					className={cn(
						"text-xs mt-0.5",
						dailyChange > 0
							? "text-green-600 dark:text-green-400"
							: dailyChange < 0
								? "text-red-500 dark:text-red-400"
								: "text-muted-foreground",
					)}
				>
					{dailyChange >= 0 ? "+" : ""}
					{formatFn(dailyChange, { skipConversion: true })} ({dailyChangePct >= 0 ? "+" : ""}
					{dailyChangePct.toFixed(2)}%)
				</p>
			)}
		</div>
	)
}

function InsightCard({
	label,
	amount,
	pct,
	color = "neutral",
}: {
	label: string
	amount: number
	pct?: number
	color?: "green" | "red" | "neutral" | "auto"
}) {
	const { format } = useCurrency()

	const resolvedColor = color === "auto" ? (amount > 0 ? "green" : amount < 0 ? "red" : "neutral") : color

	const colorCls =
		resolvedColor === "green"
			? "text-green-600 dark:text-green-400"
			: resolvedColor === "red"
				? "text-red-500 dark:text-red-400"
				: "text-sky-900 dark:text-sky-100"

	return (
		<div className="p-3 bg-white/60 dark:bg-black/20 backdrop-blur-md rounded-xl border border-sky-900/10 dark:border-sky-100/10">
			<p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{label}</p>
			<p className={cn("text-lg font-bold mt-1", colorCls)}>
				{amount >= 0 ? "+" : ""}
				{format(amount, { skipConversion: true })}
			</p>
			{pct !== undefined && (
				<p className={cn("text-xs mt-0.5", colorCls)}>
					{pct >= 0 ? "+" : ""}
					{pct.toFixed(2)}%
				</p>
			)}
		</div>
	)
}

export function PerformanceSection({ portfolioId, demo }: PerformanceSectionProps) {
	const { format } = useCurrency()
	const [period, setPeriod] = useState<PerformancePeriod>("1M")
	const [timeseries, setTimeseries] = useState<TimeSeriesPoint[]>([])
	const [summary, setSummary] = useState<PerformanceSummary | null>(null)
	const [loading, setLoading] = useState(!demo)
	const [refreshing, setRefreshing] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// Demo mode: generate data internally so the period selector works
	const demoData = useMemo(() => (demo ? generateDemoPerformance(period) : null), [demo, period])

	useEffect(() => {
		if (demoData) {
			setTimeseries(demoData.timeseries)
			setSummary(demoData.summary)
			setLoading(false)
			return
		}

		let cancelled = false
		const fetchData = async () => {
			setLoading(true)
			setError(null)
			try {
				const [ts, sum] = await Promise.all([
					getPerformanceTimeseries(period, portfolioId),
					getPerformanceSummary(period, portfolioId),
				])
				if (cancelled) return
				setTimeseries(ts.data_points)
				setSummary(sum)
			} catch (err) {
				if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load performance data")
			} finally {
				if (!cancelled) setLoading(false)
			}
		}

		fetchData()
		return () => {
			cancelled = true
		}
	}, [period, portfolioId, demoData])

	const handleRefresh = async () => {
		setRefreshing(true)
		try {
			await refreshPerformanceCache(period, portfolioId)
			const [ts, sum] = await Promise.all([
				getPerformanceTimeseries(period, portfolioId),
				getPerformanceSummary(period, portfolioId),
			])
			setTimeseries(ts.data_points)
			setSummary(sum)
			setError(null)
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to refresh")
		} finally {
			setRefreshing(false)
		}
	}

	// Compute XAxis ticks — show ~6 labels regardless of period length
	const xAxisTicks = (() => {
		if (timeseries.length <= 1) return timeseries.map((d) => d.date)
		const step = Math.max(1, Math.floor(timeseries.length / 6))
		const ticks: string[] = []
		for (let i = 0; i < timeseries.length; i += step) {
			ticks.push(timeseries[i].date)
		}
		// Always include last point
		const last = timeseries[timeseries.length - 1].date
		if (ticks[ticks.length - 1] !== last) ticks.push(last)
		return ticks
	})()

	// Gainers = positive, losers = negative — sorted by |change_pct|
	const gainers = summary
		? [...(summary.top_movers ?? [])]
				.filter((m) => m.change_pct > 0)
				.sort((a, b) => b.change_pct - a.change_pct)
				.slice(0, 3)
		: []
	const losers = summary
		? [...(summary.top_movers ?? [])]
				.filter((m) => m.change_pct < 0)
				.sort((a, b) => a.change_pct - b.change_pct)
				.slice(0, 3)
		: []

	const periodLabel = summary
		? `${summary.period_return >= 0 ? "+" : ""}${format(summary.period_return, { skipConversion: true })} (${summary.period_return_pct >= 0 ? "+" : ""}${summary.period_return_pct.toFixed(2)}%) ${period === "1W" ? "past week" : period === "1M" ? "past month" : period === "3M" ? "past 3 months" : period === "6M" ? "past 6 months" : period === "YTD" ? "year to date" : period === "1Y" ? "past year" : "all time"}`
		: null

	if (loading) {
		return (
			<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
				<CardHeader>
					<PerformanceChartSkeleton />
				</CardHeader>
			</Card>
		)
	}

	if (error) {
		return (
			<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
				<CardContent className="py-8 text-center text-sm text-red-600 dark:text-red-400">{error}</CardContent>
			</Card>
		)
	}

	return (
		<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 dark:border-sky-100/10">
			<CardContent className="pt-6 space-y-4">
				{/* Header row */}
				<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
					<div>
						<div className="flex items-center gap-1.5">
							<h2 className="font-semibold text-base text-sky-950 dark:text-sky-50">
								Portfolio Performance
							</h2>
							{!demo && (
								<button
									onClick={handleRefresh}
									disabled={refreshing || loading}
									className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
									title="Refresh cached data"
								>
									<RefreshCw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} />
								</button>
							)}
						</div>
						{periodLabel && summary && (
							<p
								className={cn(
									"text-sm mt-0.5",
									summary.period_return >= 0
										? "text-green-600 dark:text-green-400"
										: "text-red-500 dark:text-red-400",
								)}
							>
								{periodLabel}
							</p>
						)}
					</div>
					<PeriodSelector
						periods={PERFORMANCE_PERIODS}
						selected={period}
						onChange={(p) => setPeriod(p as PerformancePeriod)}
					/>
				</div>

				{/* Area chart */}
				<div className="h-48 sm:h-64 [&_.recharts-wrapper]:outline-none [&_svg]:outline-none">
					<ResponsiveContainer width="100%" height="100%">
						<AreaChart tabIndex={-1} data={timeseries} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
							<defs>
								<linearGradient id="perfGradient" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
									<stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
								</linearGradient>
							</defs>
							<CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
							<XAxis
								dataKey="date"
								ticks={xAxisTicks}
								tickFormatter={(v) => formatAxisDate(v, period)}
								tick={{ fontSize: 11 }}
							/>
							<YAxis
								domain={[(dataMin: number) => dataMin * 0.97, (dataMax: number) => dataMax * 1.03]}
								tickFormatter={(v) => format(v, { compact: true, skipConversion: true })}
								tick={{ fontSize: 11 }}
								width={60}
								allowDataOverflow={false}
							/>
							<Tooltip content={<PerformanceTooltip formatFn={format} allData={timeseries} />} />
							<Area
								type="monotone"
								dataKey="value"
								stroke="#0ea5e9"
								strokeWidth={2}
								fill="url(#perfGradient)"
							/>
						</AreaChart>
					</ResponsiveContainer>
				</div>

				{/* Insight cards */}
				{summary && (
					<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
						<InsightCard
							label="Total Return"
							amount={summary.total_return}
							pct={summary.total_return_pct}
							color="auto"
						/>
						<InsightCard
							label="Capital Gain"
							amount={summary.unrealized_gain}
							pct={summary.unrealized_gain_pct}
							color="auto"
						/>
						<InsightCard label="Dividend Income" amount={summary.dividends_received} color="neutral" />
					</div>
				)}

				{/* Top Movers */}
				{(gainers.length > 0 || losers.length > 0) && (
					<div>
						<h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
							Top Movers
						</h3>
						<div className="grid grid-cols-2 gap-3">
							{/* Gainers */}
							<div className="space-y-1.5">
								{gainers.map((m) => (
									<div
										key={`${m.ticker}:${m.exchange}`}
										className="flex items-center justify-between text-xs"
									>
										<div className="min-w-0">
											<span className="font-semibold text-foreground">{m.ticker}</span>
											<span className="ml-1 text-[10px] text-muted-foreground bg-muted px-1 rounded">
												{m.exchange}
											</span>
											<p className="text-muted-foreground truncate text-[10px]">{m.name}</p>
										</div>
										<div className="text-right ml-2 shrink-0">
											<p className="font-semibold text-green-600 dark:text-green-400">
												+{m.change_pct.toFixed(2)}%
											</p>
											<p className="text-muted-foreground text-[10px]">
												+{format(m.change_abs, { skipConversion: true })}
											</p>
										</div>
									</div>
								))}
							</div>

							{/* Losers */}
							<div className="space-y-1.5">
								{losers.map((m) => (
									<div
										key={`${m.ticker}:${m.exchange}`}
										className="flex items-center justify-between text-xs"
									>
										<div className="min-w-0">
											<span className="font-semibold text-foreground">{m.ticker}</span>
											<span className="ml-1 text-[10px] text-muted-foreground bg-muted px-1 rounded">
												{m.exchange}
											</span>
											<p className="text-muted-foreground truncate text-[10px]">{m.name}</p>
										</div>
										<div className="text-right ml-2 shrink-0">
											<p className="font-semibold text-red-500 dark:text-red-400">
												{m.change_pct.toFixed(2)}%
											</p>
											<p className="text-muted-foreground text-[10px]">
												{format(m.change_abs, { skipConversion: true })}
											</p>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	)
}
